import NotificationService, { type TNotification } from "@/api/models/message/notification";
import { useQueryClient, type QueryClient, type UseQueryReturnType } from "@tanstack/vue-query";
import { useAuthStore } from "@/stores/authStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { storeToRefs } from "pinia";
import { djangoErrorHandler } from "@/utils/DjangoError";
import { useSnackStore } from "@/stores/snackStore";
import { logError } from "@/utils/AppError";
import { watch, type MaybeRefOrGetter, type Ref } from "vue";
import { useGenericListQuery, createQueryKeys } from "@/composables/shared/queries";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

export const NotificationQueryKeys = createQueryKeys(NotificationService.queryKey, (key) => ({
  list: key("list"),
}));

/** Coalesce rapid websocket-driven refetches (e.g. treatment-plan activation). */
const NOTIFICATION_COUNT_DEDUPE_MS = 5000;

export const getNotificationIconDisplay = (
  type: TNotification["notification_type"]
): { icon: string; classes: string } => {
  switch (type) {
    case "patient_message":
      return { icon: "mdi-message-outline", classes: "tw:bg-blue-500/10 tw:text-blue-500" };
    case "treatment_plan":
      return {
        icon: "mdi-clipboard-pulse-outline",
        classes: "tw:bg-amber-400/15 tw:text-amber-500",
      };
    default:
      return { icon: "mdi-bell-outline", classes: "tw:bg-on-surface/5 tw:text-on-surface/60" };
  }
};

export const isNotificationUnread = (notification: TNotification): boolean => !notification.read_at;

const fetchNotifications = async (): Promise<TNotification[]> => {
  const response = await NotificationService.getUserNotifications({});
  return response.data ?? [];
};

export const updateUnreadNotificationCount = async (queryClient: QueryClient): Promise<void> => {
  const notificationStore = useNotificationStore();
  const snackStore = useSnackStore();
  const { unreadNotificationsCount } = storeToRefs(notificationStore);

  try {
    const list = await queryClient.fetchQuery({
      queryKey: NotificationQueryKeys.list(),
      queryFn: fetchNotifications,
      staleTime: NOTIFICATION_COUNT_DEDUPE_MS,
    });

    unreadNotificationsCount.value = list.filter((notification) =>
      isNotificationUnread(notification)
    ).length;
  } catch (error) {
    logError(error, {
      context: {
        feature: "notifications",
        step: "updateUnreadNotificationCount",
      },
    });
    djangoErrorHandler(snackStore)(error);
  }
};

export const useNotificationsQuery = (enabled: MaybeRefOrGetter<boolean> = true) =>
  useGenericListQuery<TNotification>({
    queryKey: NotificationQueryKeys.list(),
    fetcher: () => NotificationService.getUserNotifications({}),
    params: { enabled },
    justQueryKey: true,
    permission: $permSubjects.MESSAGE__NOTIFICATION,
  });

export const useMarkNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useGenericMutation<void, void>({
    mutationFn: () => NotificationService.markNotificationsAsRead({}, undefined),
    feature: "notifications.markAsRead",
    options: {
      onSuccess: () => {
        const readAt = new Date().toISOString();

        queryClient.setQueryData<TNotification[]>(NotificationQueryKeys.list(), (current) =>
          current?.map((notification) =>
            notification.read_at ? notification : { ...notification, read_at: readAt }
          )
        );
      },
    },
  });
};

export const useNotification = (): UseQueryReturnType<TNotification[], Error> & {
  unreadNotificationsCount: Ref<number>;
  resetUnreadNotificationCount: () => Promise<void>;
  updateUnreadNotificationCount: () => Promise<void>;
} => {
  const authStore = useAuthStore();
  const notificationStore = useNotificationStore();

  const { isLoggedIn } = storeToRefs(authStore);
  const { unreadNotificationsCount } = storeToRefs(notificationStore);
  const { mutateAsync: markNotificationsAsRead } = useMarkNotificationsAsRead();
  const queryClient = useQueryClient();

  const resetUnreadNotificationCount = async (): Promise<void> => {
    await markNotificationsAsRead();
    unreadNotificationsCount.value = 0;
  };

  const query = useNotificationsQuery(isLoggedIn);

  watch(
    query.data,
    (list) => {
      if (!list) return;

      unreadNotificationsCount.value = list.filter((notification) =>
        isNotificationUnread(notification)
      ).length;
    },
    { immediate: true }
  );

  return {
    ...query,
    unreadNotificationsCount,
    resetUnreadNotificationCount,
    updateUnreadNotificationCount: () => updateUnreadNotificationCount(queryClient),
  };
};
